import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { FolderOpen, Plus, MoreVertical, Pencil, Trash2, Search, AlertCircle, FileText, FileSpreadsheet } from "lucide-react";
import { ImportWizard } from "../components/ImportWizard";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";


const DOCUMENT_TYPES = [
  "Operations Manual",
  "ConOps",
  "SORA",
  "Internal Procedures",
  "Training Records",
  "Maintenance Log",
  "Insurance Certificate",
  "Other"
];

export const Documents = () => {
  const { user } = useAuth();
  const { activeOrganizationId, isPlatformAdmin, registerScopeChangeGuard } = useOrganizationScope();
  const scopedOrganizationId = isPlatformAdmin ? activeOrganizationId : user?.organization_id;
  const { t } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [formData, setFormData] = useState({
    organization_id: "",
    document_type: "",
    document_name: "",
    document_version: "1.0",
    approval_date: "",
    expiration_date: "",
    last_review_date: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [docsRes, orgsRes] = await Promise.all([
        api.get(`/documents`),
        api.get(`/organizations`)
      ]);
      setDocuments(docsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error(t.messages.fetchError);
    } finally {
      setLoading(false);
    }
  }, [ t.messages.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showDialog) return undefined;
    return registerScopeChangeGuard(() => window.confirm(t.organizationScope?.formDirtyConfirm));
  }, [showDialog, registerScopeChangeGuard, t.organizationScope?.formDirtyConfirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      organization_id: scopedOrganizationId
    };
    
    try {
      if (editingDocument) {
        await api.put(`/documents/${editingDocument.id}`, payload);
        toast.success(t.messages.documentUpdated);
      } else {
        await api.post(`/documents`, payload);
        toast.success(t.messages.documentAdded);
      }
      setShowDialog(false);
      setEditingDocument(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.messages.saveError);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`${t.actions.delete} "${doc.document_name}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(t.messages.documentDeleted);
      fetchData();
    } catch (error) {
      toast.error(t.messages.deleteError);
    }
  };

  const handleEdit = (doc) => {
    setEditingDocument(doc);
    setFormData({
      organization_id: doc.organization_id,
      document_type: doc.document_type,
      document_name: doc.document_name,
      document_version: doc.document_version,
      approval_date: doc.approval_date?.split("T")[0] || "",
      expiration_date: doc.expiration_date?.split("T")[0] || "",
      last_review_date: doc.last_review_date?.split("T")[0] || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      organization_id: scopedOrganizationId || "",
      document_type: "",
      document_name: "",
      document_version: "1.0",
      approval_date: "",
      expiration_date: "",
      last_review_date: ""
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  const getStatusBadge = (status) => {
    if (status === "valid") return <Badge className="bg-emerald-100 text-emerald-800">{t.requiredDocs.valid}</Badge>;
    if (status === "expiring") return <Badge className="bg-amber-100 text-amber-800">{t.requiredDocs.expiring}</Badge>;
    return <Badge className="bg-rose-100 text-rose-800">{t.requiredDocs.expired}</Badge>;
  };

  const filteredDocuments = documents.filter(d =>
    d.document_name.toLowerCase().includes(search.toLowerCase()) ||
    d.document_type.toLowerCase().includes(search.toLowerCase())
  );

  const expiredCount = documents.filter(d => d.document_status === "expired").length;
  const expiringCount = documents.filter(d => d.document_status === "expiring").length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.complianceModule.documentation}
          </h1>
          <p className="text-slate-500 mt-1">{t.pageDescriptions.documents}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowImportWizard(true)}
            className="gap-2"
            data-testid="import-documents-btn"
          >
            <FileSpreadsheet className="h-4 w-4" /> {t.actions?.import || 'Import'}
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingDocument(null); setShowDialog(true); }}
            className="gap-2 bg-primary hover:bg-primary/90"
            data-testid="add-document-btn"
          >
            <Plus className="h-4 w-4" /> {t.complianceModule.addDocument}
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {(expiredCount > 0 || expiringCount > 0) && (
        <div className="flex gap-4">
          {expiredCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-rose-500" />
              <span className="text-sm text-rose-700">{expiredCount} {t.complianceModule.documentsExpiredAlert}</span>
            </div>
          )}
          {expiringCount > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-amber-700">{expiringCount} {t.complianceModule.documentsExpiringAlert}</span>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.complianceModule.searchDocuments}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-documents"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.complianceModule.documentName}</TableHead>
                {user?.role === "platform_admin" && <TableHead>{t.common.organization}</TableHead>}
                <TableHead>{t.complianceModule.documentType}</TableHead>
                <TableHead>{t.complianceModule.documentVersion}</TableHead>
                <TableHead>{t.complianceModule.approvalDate}</TableHead>
                <TableHead>{t.crew.expirationDate}</TableHead>
                <TableHead>{t.complianceModule.lastReview}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.complianceModule.noDocumentsFound}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((doc) => (
                  <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400" />
                        {doc.document_name}
                      </div>
                    </TableCell>
                    {user?.role === "platform_admin" && (
                      <TableCell className="text-sm text-slate-500">{getOrgName(doc.organization_id)}</TableCell>
                    )}
                    <TableCell>
                      <Badge variant="outline">{doc.document_type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{doc.document_version}</TableCell>
                    <TableCell className="text-sm">{formatDate(doc.approval_date)}</TableCell>
                    <TableCell className="text-sm">{formatDate(doc.expiration_date)}</TableCell>
                    <TableCell className="text-sm">{formatDate(doc.last_review_date)}</TableCell>
                    <TableCell>{getStatusBadge(doc.document_status)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`document-actions-${doc.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(doc)}>
                            <Pencil className="h-4 w-4 mr-2" /> {t.actions.edit}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(doc)} className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" /> {t.actions.delete}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDocument ? t.dialogs.editDocument : t.dialogs.addDocument}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="document-form">
            {user?.role === "platform_admin" && (
              <div className="space-y-2">
                <Label>{t.common.organization} *</Label>
                <Select
                  value={formData.organization_id}
                  disabled
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger data-testid="document-organization">
                    <SelectValue placeholder={t.placeholders.selectOrganization} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.complianceModule.documentType} *</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(value) => setFormData({ ...formData, document_type: value })}
                >
                  <SelectTrigger data-testid="document-type">
                    <SelectValue placeholder={t.complianceModule.selectType} />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="doc_version">{t.complianceModule.documentVersion}</Label>
                <Input
                  id="doc_version"
                  value={formData.document_version}
                  onChange={(e) => setFormData({ ...formData, document_version: e.target.value })}
                  placeholder="1.0"
                  data-testid="document-version"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc_name">{t.complianceModule.documentName} *</Label>
              <Input
                id="doc_name"
                value={formData.document_name}
                onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                required
                placeholder={t.complianceModule.documentNamePlaceholder}
                data-testid="document-name"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="approval_date">{t.complianceModule.approvalDate}</Label>
                <Input
                  id="approval_date"
                  type="date"
                  value={formData.approval_date}
                  onChange={(e) => setFormData({ ...formData, approval_date: e.target.value })}
                  data-testid="document-approval"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiration_date">{t.crew.expirationDate}</Label>
                <Input
                  id="expiration_date"
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                  data-testid="document-expiration"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="review_date">{t.complianceModule.lastReview}</Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formData.last_review_date}
                  onChange={(e) => setFormData({ ...formData, last_review_date: e.target.value })}
                  data-testid="document-review"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>{t.actions.cancel}</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="document-submit-btn">
                {editingDocument ? t.actions.update : t.actions.add}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Wizard */}
      <ImportWizard
        open={showImportWizard}
        onOpenChange={setShowImportWizard}
        entityType="documents"
        onComplete={() => fetchData()}
      />
    </div>
  );
};
